import { useCallback, useEffect, useRef, useState } from "react";
import { errorText } from "../i18n/serverErrors";
import { useNavigate } from "react-router-dom";
import { api, getLoginAt } from "../api/apiClient";
import { primeKioskGeo } from "../api/kioskGeo";
import type {
  BouquetTemplate,
  CustomerOrder,
  FloorTodayWorkItem,
  Location,
  Zone,
} from "../api/types";
import { shopModeApi } from "../api/shopMode";
import { ShopFloorBouquetBuilder } from "../components/shop-floor/ShopFloorBouquetBuilder";
import { ShopFloorBouquetStockPanel } from "../components/shop-floor/ShopFloorBouquetStockPanel";
import { ShopFloorBoxList } from "../components/shop-floor/ShopFloorBoxList";
import { ShopFloorBouquetTemplatePanel } from "../components/shop-floor/ShopFloorBouquetTemplatePanel";
import { ShopFloorPageShell } from "../components/shop-floor/ShopFloorPageShell";
import { ShopFloorProfilePanel } from "../components/shop-floor/ShopFloorProfilePanel";
import { ShopFloorSalePanel } from "../components/shop-floor/ShopFloorSalePanel";
import { ShopFloorShiftCard } from "../components/shop-floor/ShopFloorShiftCard";
import { ShopFloorNotePanel } from "../components/shop-floor/ShopFloorNotePanel";
import { ShopFloorOrdersPanel } from "../components/shop-floor/ShopFloorOrdersPanel";
import { ShopFloorShopifyOrdersPanel } from "../components/shop-floor/ShopFloorShopifyOrdersPanel";
import { ShopFloorItemFinder } from "../components/shop-floor/ShopFloorItemFinder";
import { ShopFloorWasteFlow } from "../components/shop-floor/ShopFloorWasteFlow";
import { ShopFloorBugReport } from "../components/shop-floor/ShopFloorBugReport";
import { ShopFloorErrorBoundary } from "../components/shop-floor/ShopFloorErrorBoundary";
import { ShopFloorIcon } from "../components/shop-floor/ShopFloorIcon";
import { ShopFloorMenuButton } from "../components/shop-floor/ShopFloorMenuButton";
import { useShopFloorTodayOverview } from "../components/shop-floor/useShopFloorTodayOverview";
import { ShopFloorTasksPanel } from "../components/tasks/ShopFloorTasksPanel";
import { useAuth } from "../state/AuthContext";
import { useShopMode } from "../state/ShopModeContext";
import { useWaitingOrders } from "../hooks/useWaitingOrders";
import { useAlertChime } from "../hooks/useAlertChime";
import {
  clearDisarmIntent,
  disarmIntentAt,
  mayAutoDisarm,
  setDisarmIntent,
} from "../components/kioskAccess";
import { lockTheme, unlockTheme } from "../theme/ThemeProvider";
import { LanguageProvider, useLanguage, useT } from "../i18n/LanguageProvider";
import { money } from "../i18n/intl";
import { fromAppLanguage } from "../i18n/languages";
import type { PlainKey } from "../i18n/types";
import {
  cancelFob,
  fobAntennaHint,
  holdFobReader,
  releaseFobReader,
  fobErrorCode,
  fobErrorMessage,
  nfcStatus,
  scanFob,
} from "../platform/nfc";

/** Server-generated PIN length (owner decision 2026-08-04: 8 unique digits). */
const PIN_LENGTH = 8;

/** G-85: role → dictionary key; an unknown role shows its raw code. */
const ROLE_KEYS: Record<string, PlainKey> = {
  OWNER: "role.OWNER",
  ADMIN: "role.ADMIN",
  MANAGER: "role.MANAGER",
  FLORIST: "role.FLORIST",
};

function countPendingTodayWorkItems(items: FloorTodayWorkItem[]): number {
  return items.filter((item) =>
    item.kind === "TASK"
      ? item.task.status === "PENDING"
      : item.status === "SCHEDULED" || item.status === "IN_PROGRESS",
  ).length;
}

// ── Unarmed view ──────────────────────────────────────────────────────────────

function UnarmedView() {
  const { hasRole } = useAuth();
  const { enter, sessionRecoveryMessage } = useShopMode();
  const navigate = useNavigate();
  const { t } = useT();

  // Owner / Admin must pick a location; Manager uses their assignment server-side.
  const needsLocationPicker = hasRole("OWNER", "ADMIN");

  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [arming, setArming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!needsLocationPicker) return;
    setLocationsLoading(true);
    void (async () => {
      try {
        const list = await api.listLocations();
        const active = list.filter((l) => l.isActive);
        setLocations(active);
        if (active.length > 0) setSelectedLocationId(active[0].id);
      } catch {
        // Non-fatal — user sees an empty select and can't arm; error is implicit.
      } finally {
        setLocationsLoading(false);
      }
    })();
  }, [needsLocationPicker]);

  const handleArm = useCallback(async () => {
    setArming(true);
    setError(null);
    try {
      await enter(needsLocationPicker ? selectedLocationId || undefined : undefined);
      // Context updates isArmed → this component unmounts, ArmedPinView renders.
    } catch (err: unknown) {
      setError(errorText(err, "arm.couldNotArm"));
      setArming(false);
    }
    // `t` is GONE from these deps, and that is the point of `errorText`: it
    // reads the language at call time through the module `t`, so a callback
    // that guards a fetch no longer has to be rebuilt every time the language
    // changes. The house rule, arrived at by deleting code rather than
    // remembering it (S5d).
  }, [enter, needsLocationPicker, selectedLocationId]);

  const canArm = !needsLocationPicker || Boolean(selectedLocationId);
  const displayedError = error ?? sessionRecoveryMessage;

  return (
    <ShopFloorPageShell>
      <div className="shop-floor-arm-card">
        <div className="shop-floor-arm-icon" aria-hidden="true">🏪</div>
        <h2 className="shop-floor-arm-title">{t("arm.title")}</h2>
        <p className="shop-floor-arm-body">{t("arm.body")}</p>

        {needsLocationPicker ? (
          <div className="field shop-floor-arm-field">
            <label htmlFor="arm-location">{t("arm.shopLocation")}</label>
            <select
              id="arm-location"
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              disabled={locationsLoading || arming || locations.length === 0}
            >
              {locationsLoading ? (
                <option value="">{t("common.loading")}</option>
              ) : locations.length === 0 ? (
                <option value="">{t("arm.noActiveLocations")}</option>
              ) : (
                locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name} ({loc.code})
                  </option>
                ))
              )}
            </select>
          </div>
        ) : null}

        {displayedError ? (
          <div className="alert error shop-floor-arm-error" role="alert">
            {displayedError}
          </div>
        ) : null}

        <button
          type="button"
          className="button shop-floor-arm-btn"
          onClick={() => void handleArm()}
          disabled={arming || !canArm}
        >
          {arming ? t("arm.arming") : t("arm.armThisTablet")}
        </button>

        {/*
          The way OUT. Reported 2026-09-10: "I can't exit Kiosk mode... there is
          no button for it."

          This screen renders OUTSIDE DashboardLayout, so it has no sidebar, and
          inside the tablet app there is no browser chrome either — which made
          the unarmed kiosk a DEAD END. Disarming from the PIN screen lands the
          person here, and from here there was no route back to the rest of the
          app at all, so an owner could not even reach the Team page to issue a
          fob.

          Shown to everyone on purpose. OD-35 confines managers and florists to
          the kiosk INSIDE the shell, but confinement must never mean trapping:
          whoever opened this screen has a live staff session and is entitled to
          the screen they came from.
        */}
        <button
          type="button"
          className="button plain shop-floor-arm-leave"
          onClick={() => navigate("/app")}
          disabled={arming}
        >
          {t("arm.backToApp")}
        </button>
      </div>
    </ShopFloorPageShell>
  );
}

// ── Armed PIN-entry view ──────────────────────────────────────────────────────

function ArmedPinView() {
  const { user, endCrmSessionLocally } = useAuth();
  const { verifyPin, verifyFob, exit, shopFloorSessionId } = useShopMode();
  const { t } = useT();

  /*
   * G-79 — the shop learns that work arrived WITHOUT anybody signing in.
   *
   * Owner, 2026-09-13: "the tablet will be in the flower shop. Florists should
   * know that order arrived even without entering the pin." Every order surface
   * used to sit behind the PIN, so an order was only ever found by someone who
   * had already decided to go and look.
   *
   * Enabled only on THIS view: once a florist is signed in the real order
   * panels are on screen, and a banner would be a second, staler answer.
   */
  const { waiting, arrived, acknowledge, freshness } =
    useWaitingOrders(shopFloorSessionId);
  const waitingCount = waiting.orders.length;
  /*
   * What may be SHOWN and what may be SOUNDED are different questions.
   *
   * A stale answer is still worth showing as a last-known hint, but it must not
   * ring: during an outage an order claimed at another shop would otherwise
   * chime here every minute all night, for work that no longer exists and that
   * nobody at this tablet can clear. Before the first successful poll there is
   * nothing to show at all — "I have not managed to ask" is not "no orders".
   */
  const showBanner = waitingCount > 0 && freshness !== "unknown";
  const soundAlert = waitingCount > 0 && freshness === "fresh";

  /*
   * The sound the owner asked for: one per NEW order — including the second
   * that lands while the first is still unclaimed — and then once a minute for
   * as long as nobody takes it (their ruling, over my suggestion of escalating
   * on age). Silence returns by itself the moment the list empties: claiming at
   * ANY shop drops a Shopify order out of every other tablet's poll.
   */
  useAlertChime(soundAlert, arrived, acknowledge);

  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  /**
   * G-48: whether this tablet can read a fob at all. Absent in a browser, so
   * the "or tap your fob" line never appears where it would be a lie.
   */
  const [fobReady, setFobReady] = useState(false);
  const [fobBusy, setFobBusy] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [exitError, setExitError] = useState<string | null>(null);

  // Auto-submit when the full PIN is entered.
  useEffect(() => {
    if (pin.length !== PIN_LENGTH || submitting) return;
    setSubmitting(true);
    setPinError(null);
    void verifyPin(pin)
      .then(() => {
        setPin("");
        // isFloorAuthenticated → true; AuthenticatedView takes over.
      })
      .catch((err: unknown) => {
        setPin("");
        if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 429) {
          const retryMs = (err as { retryAfterMs?: number }).retryAfterMs;
          const secs = retryMs ? Math.ceil(retryMs / 1000) : null;
          setPinError(
            secs
              ? t("pin.tooManyAttemptsRetryIn", { count: secs })
              : t("pin.tooManyAttemptsWait"),
          );
        } else {
          setPinError(errorText(err, "pin.incorrect"));
        }
      })
      .finally(() => setSubmitting(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const handleDigit = (d: string) => {
    if (submitting || pin.length >= PIN_LENGTH) return;
    setPin((p) => p + d);
  };
  const handleBackspace = () => {
    if (submitting) return;
    setPin((p) => p.slice(0, -1));
  };
  const handleClear = () => {
    if (!submitting) setPin("");
  };

  /*
   * TAKING THE TABLET OUT OF KIOSK MODE COSTS A FRESH SIGN-IN.
   *
   * Owner scenario, 2026-09-14: a manager arms the tablet and leaves; a florist
   * presses Exit and is inside the manager's CRM, because arming never ended
   * that session. So Exit no longer disarms — it ENDS the CRM session in this
   * browser and asks for a sign-in first.
   *
   * `endCrmSessionLocally`, not `logout()`: logging out server-side would
   * REVOKE this very ShopFloorSession (`auth.service.ts:380`), disarming the
   * tablet before anyone proved who they were and making the sign-in pointless.
   *
   * The intent flag survives the round trip so this stays ONE gesture: press
   * Exit, sign in, and the tablet disarms itself on the way back.
   */
  // UNCONDITIONAL. Binding this to `user ? handleExit : requestDisarm` was
  // the bug the device test caught on 2026-09-14: a manager's LINGERING
  // session satisfies `user`, so the florist in the owner's scenario still
  // disarmed the tablet in one tap. The button never disarms; only the
  // effect below does, and only after somebody signed in.
  const requestDisarm = () => {
    setDisarmIntent();
    endCrmSessionLocally(t("pin.signInToExit"));
  };

  /*
   * The other end of that round trip. A CRM user present HERE can only have
   * come from a sign-in after Exit was pressed, because pressing Exit cleared
   * whatever session was there before.
   */
  const disarmingRef = useRef(false);
  useEffect(() => {
    if (!user || disarmingRef.current) return;
    /*
     * A CRM user being present is NOT proof somebody signed in. The httpOnly
     * refresh cookie outlives the in-memory session, so the next page load
     * restores the same one — measured on the tablet 2026-09-14, where it
     * disarmed the tablet with nobody authenticating. Only a login stamped
     * AFTER the Exit press counts; the intent is left in place otherwise, so a
     * real sign-in later still finishes the job.
     */
    if (!mayAutoDisarm(disarmIntentAt(), getLoginAt())) return;
    clearDisarmIntent();
    disarmingRef.current = true;
    void (async () => {
      setExiting(true);
      setExitError(null);
      try {
        await exit();
      } catch (err: unknown) {
        // Leave them on the kiosk rather than in limbo; Exit can be pressed
        // again, and the intent has already been consumed.
        setExitError(
          errorText(err, "pin.couldNotDisarm"),
        );
        disarmingRef.current = false;
      } finally {
        setExiting(false);
      }
    })();
  }, [user, exit, t]);

  /**
   * G-48 — the fob listener.
   *
   * The PIN screen keeps a scan armed for as long as it is showing, re-arming
   * after every attempt. That is not just convenience: Android hands a tag to
   * whichever app has reader mode active, and to the SYSTEM handler when none
   * does. Without this, tapping a fob at the kiosk opened Samsung's tag viewer
   * (owner report, 2026-09-10). Arming here is what makes the fob belong to the
   * kiosk while the kiosk is asking for a credential.
   *
   * A timeout is not an error — it means nobody tapped — so it re-arms in
   * silence. Anything else is shown once and then re-armed anyway, because a
   * florist holding a fob at a dead screen has no way forward.
   */
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const status = await nfcStatus();
      if (cancelled) return;
      setFobReady(status.supported && status.enabled);
      if (!status.supported || !status.enabled) return;

      // Sequential by design: one reader, one tap at a time.
      while (!cancelled) {
        try {
          const read = await scanFob(30_000);
          if (cancelled) return;
          setFobBusy(true);
          setPinError(null);
          await verifyFob(read.payload, read.counter);
          // Success unmounts this view (isFloorAuthenticated flips), so there
          // is nothing to re-arm.
          return;
        } catch (error) {
          if (cancelled) return;
          if (fobErrorCode(error) !== "TIMEOUT") {
            setPinError(fobErrorMessage(error));
          }
        } finally {
          if (!cancelled) setFobBusy(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      // Release the reader when this screen goes away, or it would keep
      // claiming tags behind whatever replaces it.
      void cancelFob();
    };
  }, [verifyFob]);

  return (
    <ShopFloorPageShell
      className={showBanner ? "shop-floor-page--waiting" : undefined}
    >
      {/*
        The honest unknown, in its two flavours. A dropped poll is NOT evidence
        that nothing is waiting, and the first version swallowed the error and
        left a calm, empty screen — indistinguishable from a quiet shop. Muted
        rather than pink on purpose: this is "I cannot see", not "come quickly",
        and dressing it as an alarm would train florists to ignore the real one.
      */}
      {freshness === "fresh" ? null : (
        <p className="shop-floor-waiting-offline muted" role="status">
          {freshness === "unknown"
            ? t("pin.checkingForOrders")
            : t("pin.cannotCheckOrders")}
        </p>
      )}
      {/*
        Deliberately says the COUNT and the MONEY and nothing else — no customer,
        no address, no items. This is the one banner in the product that a
        passer-by can read without signing in, so it carries the least that is
        still worth crossing the shop for. `aria-live` announces it to a screen
        reader the moment it appears, rather than only on the next focus.
      */}
      {showBanner ? (
        <div className="shop-floor-waiting-banner" role="status" aria-live="polite">
          <p className="shop-floor-waiting-banner__headline">
            {t("pin.ordersWaiting", { count: waitingCount })}
          </p>
          <strong className="shop-floor-waiting-banner__amount">
            {waiting.totalIsPartial
              ? t("pin.waitingAmountAtLeast", { amount: money(waiting.totalEur) })
              : t("pin.waitingAmount", { amount: money(waiting.totalEur) })}
          </strong>
          {/* Says plainly that the figure is a memory, not a reading. */}
          {freshness === "stale" ? (
            <p className="shop-floor-waiting-banner__stale muted">
              {t("pin.lastSeenBeforeDrop")}
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="shop-floor-pin-card">
        <h2 className="shop-floor-pin-title">{t("pin.title")}</h2>
        <p className="shop-floor-pin-subtitle muted">
          {/*
            KEPT SHORT. This card is max-width 340px and G-62 already had to
            shrink it to fit the small tablet, so a long sentence here pushes
            the PIN dots and numpad down the screen. The WHERE lives on the
            hint line below, next to "waiting for a fob", which is where a
            florist is actually looking when the tap is not working.
          */}
          {fobReady ? t("pin.subtitleFob") : t("pin.subtitle")}
        </p>
        {fobReady ? (
          <p
            className="shop-floor-pin-fob-hint muted"
            aria-live="polite"
          >
            {fobBusy
              ? t("pin.readingFob")
              : t("pin.holdAgainst", { where: fobAntennaHint() })}
          </p>
        ) : null}

        {/* PIN dots */}
        <div
          className="shop-floor-pin-dots"
          aria-label={t("pin.digitsEntered", { entered: pin.length, total: PIN_LENGTH })}
          aria-live="polite"
        >
          {Array.from({ length: PIN_LENGTH }, (_, i) => i).map((i) => (
            <span
              key={i}
              className={`shop-floor-pin-dot${pin.length > i ? " shop-floor-pin-dot--filled" : ""}`}
            />
          ))}
        </div>

        {pinError ? (
          <div className="alert error shop-floor-pin-error" role="alert">
            {pinError}
          </div>
        ) : null}

        {/* Numpad — 3×3 grid (1-9) + C + 0 + ⌫ */}
        <div className="shop-floor-numpad" role="group" aria-label={t("pin.numberPad")}>
          {(["1", "2", "3", "4", "5", "6", "7", "8", "9"] as const).map((d) => (
            <button
              key={d}
              type="button"
              className="shop-floor-numpad-btn"
              onClick={() => handleDigit(d)}
              disabled={submitting || pin.length >= PIN_LENGTH}
              aria-label={d}
            >
              {d}
            </button>
          ))}
          {/* Bottom row: Clear, 0, Backspace */}
          <button
            type="button"
            className="shop-floor-numpad-btn shop-floor-numpad-btn--aux"
            onClick={handleClear}
            disabled={submitting}
            aria-label={t("pin.clear")}
          >
            C
          </button>
          <button
            type="button"
            className="shop-floor-numpad-btn"
            onClick={() => handleDigit("0")}
            disabled={submitting || pin.length >= PIN_LENGTH}
            aria-label="0"
          >
            0
          </button>
          <button
            type="button"
            className="shop-floor-numpad-btn shop-floor-numpad-btn--aux"
            onClick={handleBackspace}
            disabled={submitting}
            aria-label={t("pin.backspace")}
          >
            ⌫
          </button>
        </div>

        {submitting ? (
          <p className="muted shop-floor-pin-checking" aria-live="polite">
            {t("pin.checking")}
          </p>
        ) : null}

        {/* Always offered, and it always costs a sign-in — see requestDisarm. */}
        <div className="shop-floor-pin-footer">
          {exitError ? (
            <div className="alert error" style={{ width: "100%" }}>
              {exitError}
            </div>
          ) : null}
          <button
            type="button"
            className="button plain"
            onClick={requestDisarm}
            disabled={exiting}
          >
            {exiting ? t("pin.disarming") : t("pin.exitKioskMode")}
          </button>
        </div>
      </div>
    </ShopFloorPageShell>
  );
}

// ── Authenticated worker view ─────────────────────────────────────────────────

/** G-75: "find" is where See stock now lands — search first, zones after. */
/**
 * Owner ruling 2026-09-15 (evening): the per-zone tabs are gone. "Find an
 * item" already answers "where is it" in every row, so a zone as a tab was a
 * precondition to a question it no longer needed to be. Two views remain.
 */
/** G-84 (OD-51): "boxes" — the searchable list of this shop's storage boxes. */
type StockStep = "find" | "bouquets" | "boxes";
type HubView =
  | "sale"
  | "build-bouquet"
  | "tasks"
  | "orders"
  | "note"
  | "stock"
  | "waste";

function AuthenticatedView() {
  const { floorActor, lockPin, screenAwake } = useShopMode();
  const { t } = useT();
  const [locking, setLocking] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const [bouquetOpen, setBouquetOpen] = useState(false);
  const [bouquetInitialTemplate, setBouquetInitialTemplate] =
    useState<BouquetTemplate | null>(null);
  const [hubView, setHubView] = useState<HubView>("tasks");
  /**
   * G-62: on a small kiosk tablet the till and the stock browser take the WHOLE
   * canvas and the action menu collapses to a hamburger (owner ruling
   * 2026-09-10). Open here means the menu is showing as an overlay drawer over
   * one of those two views; it is meaningless on a roomy screen, where the
   * sidebar is always visible and CSS ignores this class.
   */
  const [orderNotificationCount, setOrderNotificationCount] = useState(0);
  const [taskNotificationCount, setTaskNotificationCount] = useState(0);
  const [shopifyBuildTarget, setShopifyBuildTarget] = useState<{
    orderId: string;
    lineId: string;
    templateId?: string | null;
    title?: string;
  } | null>(null);
  const [customerOrderBuildTarget, setCustomerOrderBuildTarget] =
    useState<CustomerOrder | null>(null);
  const [stockStep, setStockStep] = useState<StockStep>("find");
  /**
   * G-84: the shop's zones, wanted ONLY by the box forms — issuing a box asks
   * where it is kept, and taking stock out of one asks which shelf it goes
   * back to. Fetched when See stock opens and never awaited: the finder is
   * the screen a florist came for, and it needs no zones since G-75.
   */
  const [shopZones, setShopZones] = useState<Zone[]>([]);
  const shiftToday = useShopFloorTodayOverview(profileOpen);

  const refreshOrderNotifications = useCallback(async () => {
    try {
      const [todayRows, availableRows, mineRows] = await Promise.all([
        shopModeApi.floorListTodayOrders(),
        shopModeApi.floorListAvailableShopifyOrders(),
        shopModeApi.floorListMyShopifyOrders(),
      ]);
      const mirroredShopifyOrderIds = new Set(
        todayRows.flatMap((order) =>
          order.shopifyOrder ? [order.shopifyOrder.id] : [],
        ),
      );
      const unmirroredShopifyCount = [...availableRows, ...mineRows].filter(
        (order) => !mirroredShopifyOrderIds.has(order.id),
      ).length;
      setOrderNotificationCount(todayRows.length + unmirroredShopifyCount);
    } catch {
      // Preserve the most recent count during a transient refresh failure.
    }
  }, []);

  const updateTaskNotificationCount = useCallback(
    (items: FloorTodayWorkItem[]) => {
      setTaskNotificationCount(countPendingTodayWorkItems(items));
    },
    [],
  );

  const refreshTaskNotifications = useCallback(async () => {
    try {
      updateTaskNotificationCount(await shopModeApi.floorListTodayWorkItems());
    } catch {
      // Preserve the most recent count during a transient refresh failure.
    }
  }, [updateTaskNotificationCount]);

  useEffect(() => {
    void refreshOrderNotifications();
    const timer = window.setInterval(() => void refreshOrderNotifications(), 10000);
    return () => window.clearInterval(timer);
  }, [refreshOrderNotifications]);

  useEffect(() => {
    const tasksPanelVisible = hubView === "tasks" && !profileOpen && !bouquetOpen;
    if (tasksPanelVisible) return;

    void refreshTaskNotifications();
    const timer = window.setInterval(() => void refreshTaskNotifications(), 30_000);
    return () => window.clearInterval(timer);
  }, [bouquetOpen, hubView, profileOpen, refreshTaskNotifications]);

  if (!floorActor) return null;

  const handleLock = async () => {
    setLocking(true);
    try {
      await lockPin();
      // isFloorAuthenticated → false; ArmedPinView takes over.
    } finally {
      setLocking(false);
    }
  };

  const selectHubView = (view: HubView) => {
    setProfileOpen(false);
    setHubView(view);
    // Picking an action from the compact drawer closes it — the florist asked
    // for that screen, not for the menu to stay in the way of it.
  };

  /**
   * G-62: the two screens that need the whole tablet. The till's catalogue and
   * ticket are a two-column layout that collapses (and buries the ticket ~2200px
   * below the products) once the 300px action sidebar has taken its third of a
   * 960px screen; the stock browser has the same appetite. Same call the bouquet
   * checkout already made on 2026-08-09 — the work gets the canvas.
   */
  const wideCanvas =
    hubView === "sale" || hubView === "stock" || hubView === "note";


  const handleEndShift = async () => {
    await shiftToday.endShift();
    await lockPin();
  };

  const handleSeeStock = () => {
    setStockStep("find");
    selectHubView("stock");
    // Failure is swallowed on purpose: without zones the Boxes tab still
    // lists and opens boxes, and only the two forms that need a zone say so.
    void shopModeApi
      .floorLocation()
      .then((loc) => setShopZones(loc.zones.filter((z) => z.isActive)))
      .catch(() => setShopZones([]));
  };

  const openBouquetGallery = () => {
    selectHubView("build-bouquet");
  };

  const openBouquetBuilder = (template: BouquetTemplate | null) => {
    setShopifyBuildTarget(null);
    setCustomerOrderBuildTarget(null);
    setBouquetInitialTemplate(template);
    setBouquetOpen(true);
  };

  const workerSummary = (
    <>
      <button
        type="button"
        className={`shop-floor-sidebar-profile${profileOpen ? " is-open" : ""}`}
        onClick={() => setProfileOpen(true)}
        aria-expanded={profileOpen}
        aria-label={t("hub.openShiftProfile", { name: floorActor.fullName })}
      >
        <div className="shop-floor-worker-row shop-floor-tablet-worker-row">
          <div className="shop-floor-worker-avatar" aria-hidden="true">
            {floorActor.fullName.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="shop-floor-worker-name">{floorActor.fullName}</p>
            <p className="shop-floor-worker-role muted">
              {ROLE_KEYS[floorActor.role] ? t(ROLE_KEYS[floorActor.role]) : floorActor.role}
            </p>
          </div>
          <ShopFloorIcon
            name="chevron-right"
            size={22}
            className="shop-floor-profile-disclosure"
          />
        </div>
      </button>

      <div className="shop-floor-sidebar-info-list">
        <div className="shop-floor-sidebar-info-row">
          <span className="shop-floor-sidebar-info-icon">
            <ShopFloorIcon name="calendar" size={22} />
          </span>
          <div className="shop-floor-sidebar-info-copy">
            <ShopFloorShiftCard
              overview={shiftToday.overview}
              loading={shiftToday.loading}
              error={shiftToday.error}
            />
          </div>
        </div>

        <div className="shop-floor-sidebar-info-row">
          <span className="shop-floor-sidebar-info-icon">
            <ShopFloorIcon name="lock" size={22} />
          </span>
          <p className="shop-floor-idle-hint muted">
            {t("hub.idleHint")}
            {/*
              Said ONLY when the screen is not actually being held awake. A
              working kiosk stays quiet; a tablet that will dim mid-shift says
              so, rather than letting the shop discover it at the counter.
            */}
            {screenAwake.held ? null : (
              <>
                {" "}
                <strong>
                  {screenAwake.supported
                    ? t("hub.dimWarningTablet")
                    : t("hub.dimWarningBrowser")}
                </strong>
              </>
            )}
          </p>
        </div>
      </div>
    </>
  );

  const actionMenu = (
    <>

      <div className="shop-floor-action-stack shop-floor-hub-stack shop-floor-tablet-action-stack">
        <ShopFloorMenuButton
          icon="add-sale"
          label={t("hub.addSale")}
          onClick={() => selectHubView("sale")}
          disabled={locking}
          selected={hubView === "sale"}
        />
        <ShopFloorMenuButton
          icon="bouquets"
          label={t("hub.buildBouquet")}
          onClick={openBouquetGallery}
          disabled={locking}
          selected={hubView === "build-bouquet"}
        />
        <ShopFloorMenuButton
          icon="tasks"
          label={t("hub.tasks")}
          className="shop-floor-notification-menu-button"
          onClick={() => selectHubView("tasks")}
          disabled={locking}
          selected={hubView === "tasks"}
          notificationCount={taskNotificationCount}
        />
        <ShopFloorMenuButton
          icon="orders"
          label={t("hub.orders")}
          className="shop-floor-notification-menu-button shop-floor-orders-menu-button"
          onClick={() => selectHubView("orders")}
          disabled={locking}
          selected={hubView === "orders"}
          notificationCount={orderNotificationCount}
        />
        <ShopFloorMenuButton
          icon="pen"
          label={t("hub.printNote")}
          onClick={() => selectHubView("note")}
          disabled={locking}
          selected={hubView === "note"}
        />
        <ShopFloorMenuButton
          icon="stock"
          label={t("hub.seeStock")}
          className="shop-floor-see-stock-btn"
          onClick={() => void handleSeeStock()}
          disabled={locking}
          selected={hubView === "stock"}
        />
        <ShopFloorMenuButton
          icon="waste"
          label={t("hub.waste")}
          onClick={() => selectHubView("waste")}
          disabled={locking}
          selected={hubView === "waste"}
        />
        <ShopFloorMenuButton
          icon="lock"
          label={locking ? t("hub.locking") : t("hub.lockScreen")}
          variant="lock"
          className="shop-floor-lock-btn shop-floor-hub-lock-btn"
          onClick={() => void handleLock()}
          disabled={locking}
        />
      </div>
    </>
  );

  const hubScreen = (
    <div
      className={[
        "shop-floor-tablet-layout",
        wideCanvas ? "shop-floor-tablet-layout--wide" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* G-72 — at the layout root and positioned fixed, so it is on EVERY
          kiosk view. (It was placed here to escape the old G-62 rail, which
          was display:none outside wide-canvas views; the rail is now always
          visible, and this still belongs outside the error boundary below.) */}
      <ShopFloorBugReport screen={profileOpen ? "profile" : hubView} />
      <main
        className="shop-floor-tablet-tasks"
        aria-label={
          profileOpen
            ? t("hub.screenProfile")
            : hubView === "sale"
            ? t("hub.addSale")
            : hubView === "build-bouquet"
              ? t("hub.buildBouquet")
            : hubView === "stock"
              ? stockStep === "find"
                ? t("hub.findItem")
                : t("hub.bouquetsInStock")
            : hubView === "waste"
              ? t("hub.recordWaste")
            : hubView === "orders"
              ? t("hub.ordersToPrepare")
            : hubView === "note"
              ? t("hub.printNote")
              : t("hub.tasksOfTheDay")
        }
      >
        {/* G-72: the panels are inside the boundary, the rail with the Report
            button is OUTSIDE it. When a panel dies, the florist must still be
            able to file — and the boundary files one itself regardless. */}
        <ShopFloorErrorBoundary screen={profileOpen ? "profile" : hubView}>
        {profileOpen ? (
          <ShopFloorProfilePanel
            actor={floorActor}
            overview={shiftToday.overview}
            loading={shiftToday.loading}
            error={shiftToday.error}
            onClose={() => setProfileOpen(false)}
            onEndShift={handleEndShift}
          />
        ) : hubView === "sale" ? (
          <div className="shop-floor-sale-workspace">
            <ShopFloorSalePanel />
          </div>
        ) : hubView === "build-bouquet" ? (
          <div className="shop-floor-bouquet-template-workspace">
            <ShopFloorBouquetTemplatePanel
              onBuildNew={() => openBouquetBuilder(null)}
              onSelectTemplate={openBouquetBuilder}
            />
          </div>
        ) : hubView === "stock" ? (
          <div className="shop-floor-stock-workspace">
            <div className="card shop-floor-stock-panel">
              <div className="shop-floor-stock-tabs" role="tablist" aria-label={t("hub.stockViews")}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={stockStep === "find"}
                  className={`shop-floor-stock-tab${stockStep === "find" ? " is-active" : ""}`}
                  onClick={() => setStockStep("find")}
                >
                  {t("hub.findItem")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={stockStep === "bouquets"}
                  className={`shop-floor-stock-tab${stockStep === "bouquets" ? " is-active" : ""}`}
                  onClick={() => setStockStep("bouquets")}
                >
                  {t("hub.bouquets")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={stockStep === "boxes"}
                  className={`shop-floor-stock-tab${stockStep === "boxes" ? " is-active" : ""}`}
                  onClick={() => setStockStep("boxes")}
                >
                  {t("hub.boxes")}
                </button>
              </div>

              <div className="shop-floor-stock-view">
                {stockStep === "find" ? (
                  <ShopFloorItemFinder shelfZones={shopZones} />
                ) : stockStep === "boxes" ? (
                  <ShopFloorBoxList shelfZones={shopZones} />
                ) : (
                  <div className="shop-floor-stock-flow">
                    <ShopFloorBouquetStockPanel />
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : hubView === "waste" ? (
          <div className="shop-floor-stock-workspace shop-floor-waste-workspace">
            <div className="card shop-floor-stock-panel shop-floor-waste-panel">
              <ShopFloorWasteFlow
                onExit={() => setHubView("tasks")}
                showBack={false}
              />
            </div>
          </div>
        ) : hubView === "note" ? (
          <div className="shop-floor-note-workspace">
            <ShopFloorNotePanel />
          </div>
        ) : hubView === "orders" ? (
          <div className="shop-floor-orders-workspace">
            <ShopFloorOrdersPanel
              onBuildOrder={(order) => {
                setBouquetInitialTemplate(null);
                setShopifyBuildTarget(null);
                setCustomerOrderBuildTarget(order);
                setBouquetOpen(true);
              }}
            />
            <ShopFloorShopifyOrdersPanel
              onBuildLine={(target) => {
                setBouquetInitialTemplate(null);
                setCustomerOrderBuildTarget(null);
                setShopifyBuildTarget(target);
                setBouquetOpen(true);
              }}
            />
          </div>
        ) : (
          <ShopFloorTasksPanel onWorkItemsChanged={updateTaskNotificationCount} />
        )}
        </ShopFloorErrorBoundary>
      </main>
      <aside className="shop-floor-tablet-sidebar" aria-label={t("hub.actions")}>
        <div className="shop-floor-auth-card shop-floor-tablet-sidebar-card">
          {workerSummary}
          {/* NO language control in the hub — OD-59, and the owner ruled it
              TWICE, to two sessions that had not spoken. S6, 2026-09-20: once
              a florist has PINned in the language is theirs, from their own
              record, and the only picker is on the lock screen. Then again on
              2026-09-21, looking at the real screen: *"why do we still have
              those? I remember we removed language selector from
              navigation"*. A second control inside the shift is a way to
              leave a shared tablet in a language the next person did not
              choose. */}
          {actionMenu}
        </div>
      </aside>
    </div>
  );

  return (
    <ShopFloorPageShell className="shop-floor-page--tablet-hub" languageSwitcher={false}>
      {bouquetOpen ? (
        <ShopFloorBouquetBuilder
          initialTemplate={bouquetInitialTemplate}
          onExit={() => {
            setBouquetOpen(false);
            setBouquetInitialTemplate(null);
            setShopifyBuildTarget(null);
            setCustomerOrderBuildTarget(null);
          }}
          shopifyTarget={
            shopifyBuildTarget
              ? {
                  ...shopifyBuildTarget,
                  onBuilt: () => {
                    setBouquetOpen(false);
                    setBouquetInitialTemplate(null);
                    setShopifyBuildTarget(null);
                    setHubView("orders");
                    void refreshOrderNotifications();
                  },
                }
              : undefined
          }
          orderTarget={
            customerOrderBuildTarget
              ? {
                  order: customerOrderBuildTarget,
                  // OD-31: do NOT close on save — the builder stays on its
                  // post-save screen (figures + photos, no sell controls)
                  // and the florist leaves via "Done", which is onExit.
                  // Closing here used to skip the photo moment entirely.
                  onBuilt: () => {
                    setHubView("orders");
                    void refreshOrderNotifications();
                  },
                }
              : undefined
          }
          onSold={() => {
            // G-46 GAP 2: the sale completed inside the bouquet panel — no
            // screen change (owner decision 1); notifications may care.
            void refreshOrderNotifications();
          }}
        />
      ) : (
        hubScreen
      )}
    </ShopFloorPageShell>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function InTheShopRoute() {
  const { sessionChecked, isArmed, isFloorAuthenticated, floorActor } =
    useShopMode();
  const { t } = useT();
  const { setPersonLanguage, clearSession } = useLanguage();

  /*
   * G-85 S6 — the person's own language, applied the moment their PIN is
   * accepted and dropped the moment the screen locks.
   *
   * `floorActor` is null whenever nobody is signed in, so ONE effect covers
   * both directions: PIN in and the kiosk becomes theirs; lock, idle-out or
   * hard-cap expiry and it goes back to whatever the tablet shows — which is
   * also what clears any lock-screen override, so the next florist never
   * inherits the last one's choice.
   */
  useEffect(() => {
    if (floorActor) {
      setPersonLanguage(fromAppLanguage(floorActor.language));
    } else {
      clearSession();
    }
  }, [floorActor, setPersonLanguage, clearSession]);

  // G-9.0 + owner decision 2026-07-28: the kiosk is ALWAYS light — no matter
  // the device preference or live OS scheme flips (FINALIZING.md D-17: the
  // floor surface has no dark parity yet). lockTheme wins over every stamp
  // the ThemeProvider makes while the kiosk route is mounted; leaving the
  // kiosk restores the device preference. Remove when G-9.3 item 1 migrates
  // the floor hub onto the shared tokens.
  useEffect(() => {
    lockTheme("light");
    return () => unlockTheme();
  }, []);

  // G-18 (review 2026-08-19): surface the browser's location prompt on page
  // load, not mid-arming — an unanswered prompt at arm time outlives the
  // capture timeout and stamps the whole 12 h session UNKNOWN.
  useEffect(() => {
    primeKioskGeo();
  }, []);

  /*
   * HOLD THE NFC READER FOR THE WHOLE KIOSK PAGE, not just while scanning.
   *
   * Owner report 2026-09-14: holding a fob on the antenna for about a second
   * opened Samsung's tag reader over the kiosk. Reader mode is the only thing
   * suppressing Android's tag dispatch, and it used to be dropped the instant
   * the fob had been read — leaving the whole `verifyFob` round trip exposed
   * with the tag still on the glass. Once a florist was PIN'd in it was worse:
   * nothing armed the reader on the floor screens at all, so EVERY tap went to
   * Samsung.
   *
   * Held at PAGE level deliberately, not per view, because the gap was between
   * views. The plugin swallows tags nobody asked for, so this reads nothing —
   * it only stops another app being handed the tag.
   */
  useEffect(() => {
    void holdFobReader();
    return () => {
      void releaseFobReader();
    };
  }, []);

  if (!sessionChecked) {
    return (
      <ShopFloorPageShell>
        <p className="muted">{t("common.loading")}</p>
      </ShopFloorPageShell>
    );
  }

  if (isFloorAuthenticated) return <AuthenticatedView />;
  if (isArmed) return <ArmedPinView />;
  return <UnarmedView />;
}

/**
 * G-85: the language provider wraps the KIOSK ROUTE ONLY, the same scope as
 * `lockTheme` above — the rest of the CRM stays English (store back to "en"
 * on unmount, `<html lang>` restored).
 */
export function InTheShopPage() {
  return (
    <LanguageProvider>
      <InTheShopRoute />
    </LanguageProvider>
  );
}
